import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import 'antd/dist/antd.css';

/**
 * Intervalo de verificacao da versao publicada no CloudFront.
 * Em milissegundos, determina a frequencia do polling de atualizacao.
 */
const APP_UPDATE_CHECK_INTERVAL_MS = 60000;

const AutoUpdateNoCache = () => {
  const currentBuildSignature = useRef(null);

  useEffect(() => {
    /**
     * Recupera uma assinatura da build atual publicada.
     * Prioriza o asset-manifest.json e usa index.html como fallback
     * para evitar falha durante janelas curtas de deploy.
     *
     * @returns Assinatura da build atual ou null quando indisponivel.
     */
    const getBuildSignature = async () => {
      try {
        // Tenta buscar o asset-manifest.json com bypass de cache
        const response = await fetch(`/asset-manifest.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'cache-control': 'no-cache',
          },
        });

        if (!response.ok) {
          console.warn(
            `[AutoUpdate] asset-manifest.json indisponivel (${response.status}), tentando fallback`,
          );

          // Fallback: tenta buscar index.html como indicador de novo deploy
          const indexResponse = await fetch(`/?t=${Date.now()}`, {
            cache: 'no-store',
            headers: {
              'cache-control': 'no-cache',
            },
          });

          return indexResponse.ok ? `index-${Date.now()}` : null;
        }

        const manifest = await response.json();
        const mainJs = manifest?.files?.['main.js'];
        const entrypoints = Array.isArray(manifest?.entrypoints)
          ? manifest.entrypoints.join('|')
          : '';

        return mainJs || entrypoints || null;
      } catch (error) {
        console.warn(`[AutoUpdate] Erro ao verificar versao:`, error);
        return null;
      }
    };

    /**
     * Compara a assinatura atual do cliente com a assinatura publicada.
     * Quando detecta mudanca de versao, recarrega a aplicacao para
     * garantir que o totem use o build mais recente.
     */
    const checkForNewVersion = async () => {
      try {
        const latestBuildSignature = await getBuildSignature();

        if (!latestBuildSignature) {
          return;
        }

        if (!currentBuildSignature.current) {
          currentBuildSignature.current = latestBuildSignature;
          console.log(
            `[AutoUpdate] Versao inicial registrada:`,
            latestBuildSignature,
          );
          return;
        }

        if (currentBuildSignature.current !== latestBuildSignature) {
          console.log(
            `[AutoUpdate] Nova versao detectada! Recarregando aplicacao...`,
          );
          window.location.reload();
        }
      } catch (error) {
        console.error(`[AutoUpdate] Erro ao verificar atualizacao:`, error);
      }
    };

    // Executa verificacao inicial
    checkForNewVersion();

    // Agenda verificacoes periodicas
    const intervalId = window.setInterval(
      checkForNewVersion,
      APP_UPDATE_CHECK_INTERVAL_MS,
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return null;
};

ReactDOM.render(
  <React.StrictMode>
    <AutoUpdateNoCache />
    <App />
  </React.StrictMode>,
  document.getElementById('root')
);
